import { Divider } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { Controller, useFormContext } from 'react-hook-form';
import { Form, Ref } from 'semantic-ui-react';
import { ReactElement } from 'react';
import { FormDropdown } from '../../../../../../components/FormDropdown';
import { FORM_FIELD_NAMES, SUB_SECTOR_PLACEHOLDERS, FORM_CATEGORY_VALUES } from '../AddPreferencesModal.constants';
import { FilterFieldOptions } from '../AddPreferencesModal.types';
import { Preference } from '../../../../../../models/Preference';
import styles from './AddPreferencesFormContainer.module.scss';

const cx = classNames.bind(styles);

interface AddPreferencesFormProps {
  onChange: (name: string, value: string) => void;
  dropdownOptions: FilterFieldOptions;
  selectedPreference?: Preference;
}

const AddPreferencesFormContainer = ({
  onChange,
  dropdownOptions,
  selectedPreference,
}: AddPreferencesFormProps): ReactElement => {
  const { control, getValues } = useFormContext();

  const sectorSelected = Boolean(getValues(FORM_FIELD_NAMES.sector));
  const isInterestsCategorySelected = getValues(FORM_FIELD_NAMES.category) === FORM_CATEGORY_VALUES.Interests.value;
  const isReportsCategorySelected = getValues(FORM_FIELD_NAMES.category) === FORM_CATEGORY_VALUES.Reports.value;
  const isAnalystsCategorySelected = getValues(FORM_FIELD_NAMES.category) === FORM_CATEGORY_VALUES.Analysts.value;

  const subsectorPlaceholderText = sectorSelected
    ? SUB_SECTOR_PLACEHOLDERS.sectorSelected
    : SUB_SECTOR_PLACEHOLDERS.sectorNotSelected;

  return (
    <Form className={cx('body')}>
      {!selectedPreference && (
        <>
          <Controller
            name={FORM_FIELD_NAMES.category}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Categories"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.category, value)}
                  options={[
                    FORM_CATEGORY_VALUES.Interests,
                    FORM_CATEGORY_VALUES.Reports,
                    FORM_CATEGORY_VALUES.Analysts,
                  ]}
                  fluid
                  dataTestId="category-dropdown"
                  clearable={false}
                  search={false}
                />
              </Ref>
            )}
          />
          <Divider />
        </>
      )}
      {isInterestsCategorySelected && (
        <>
          <Controller
            name={FORM_FIELD_NAMES.region}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Regions"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.region, value)}
                  options={dropdownOptions[FORM_FIELD_NAMES.region]}
                  placeholder="Select Region"
                  fluid
                  dataTestId="region-dropdown"
                />
              </Ref>
            )}
          />
          <Controller
            name={FORM_FIELD_NAMES.sector}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Sectors"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.sector, value)}
                  options={dropdownOptions[FORM_FIELD_NAMES.sector]}
                  placeholder="Select Sector"
                  fluid
                  dataTestId="sector-dropdown"
                />
              </Ref>
            )}
          />
          <Controller
            name={FORM_FIELD_NAMES.subsector}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Subsectors"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.subsector, value)}
                  options={dropdownOptions[FORM_FIELD_NAMES.subsector]}
                  placeholder={subsectorPlaceholderText}
                  disabled={!sectorSelected}
                  fluid
                  dataTestId="subsector-dropdown"
                />
              </Ref>
            )}
          />
          <Controller
            name={FORM_FIELD_NAMES.creditGrade}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Credit Grades"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.creditGrade, value)}
                  options={dropdownOptions[FORM_FIELD_NAMES.creditGrade]}
                  placeholder="Select Credit Grade"
                  name={FORM_FIELD_NAMES.creditGrade}
                  fluid
                  dataTestId="credit-grade-dropdown"
                />
              </Ref>
            )}
          />
          <Controller
            name={FORM_FIELD_NAMES.assetClass}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Asset Class"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.assetClass, value)}
                  options={dropdownOptions[FORM_FIELD_NAMES.assetClass]}
                  placeholder="Select Asset Class"
                  name={FORM_FIELD_NAMES.assetClass}
                  fluid
                  dataTestId="asset-class-dropdown"
                />
              </Ref>
            )}
          />
          <Controller
            name={FORM_FIELD_NAMES.topic}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Ref innerRef={ref}>
                <Form.Field
                  {...rest}
                  label="Topics"
                  control={FormDropdown}
                  onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.topic, value)}
                  options={dropdownOptions[FORM_FIELD_NAMES.topic]}
                  placeholder="Select Topic"
                  fluid
                  dataTestId="topic-dropdown"
                />
              </Ref>
            )}
          />
        </>
      )}
      {isReportsCategorySelected && (
        <Controller
          name={FORM_FIELD_NAMES.report}
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Ref innerRef={ref}>
              <Form.Field
                {...rest}
                label="Report Name"
                control={FormDropdown}
                onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.report, value)}
                options={dropdownOptions[FORM_FIELD_NAMES.report]}
                placeholder="Select Report"
                fluid
                dataTestId="reports-dropdown"
              />
            </Ref>
          )}
        />
      )}
      {isAnalystsCategorySelected && (
        <Controller
          name={FORM_FIELD_NAMES.analyst}
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Ref innerRef={ref}>
              <Form.Field
                {...rest}
                label="Analysts"
                control={FormDropdown}
                onOptionSelect={(value) => onChange(FORM_FIELD_NAMES.analyst, value)}
                options={dropdownOptions[FORM_FIELD_NAMES.analyst]}
                placeholder="Select Analyst"
                fluid
                dataTestId="analysts-dropdown"
              />
            </Ref>
          )}
        />
      )}
    </Form>
  );
};

export default AddPreferencesFormContainer;
